<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useConnectionTemplates } from "@/queries/connection-templates";
import DataView from "@/components/DataView.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import type { ConnectionTemplateListItem } from "@/types";
import type { Config } from "datatables.net";

const router = useRouter();
const { templates, templatesLoading: loading, templatesError: error, refetchTemplates } = useConnectionTemplates();

const searchQuery = ref("");

const filtered = computed(() => {
  let list = templates.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter((t) => t.name.toLowerCase().includes(q) || (t.description ?? "").toLowerCase().includes(q));
  }
  return [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
});

const tableOptions = {
  paging: false,
  columns: [
    { data: "id", title: "ID", visible: false },
    { data: "name", title: "Name" },
  ],
  layout: { topEnd: "", bottomEnd: "", bottomStart: "" },
} as Config;

function pick(t: ConnectionTemplateListItem) {
  router.push(`/connections/new/${t.id}`);
}
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <PageHeader
          title="New Connection"
          :breadcrumbs="[
            { label: 'Dashboard', href: '/' },
            { label: 'Connections', href: '/connections' },
            { label: 'New' },
          ]"
        />

        <div class="mb-4">
          <input
            type="text"
            class="form-control"
            placeholder="Search templates..."
            v-model="searchQuery"
            data-cy="search-templates"
          />
        </div>

        <SkeletonLoader v-if="loading || error" type="grid" :cards="6" :grid-columns="3" :error="error" @retry="refetchTemplates" />

        <DataView
          v-else
          :data="filtered"
          :view-mode="'grid'"
          :options="tableOptions"
          @row-click="(t: ConnectionTemplateListItem) => pick(t)"
        >
          <template #grid-card="{ row: t }">
            <div class="card h-100 card-hover" :data-cy="`template-card-${t.id}`">
              <div class="card-body d-flex flex-column">
                <h2 class="card-title mb-2 h5 d-flex align-items-center gap-2">
                  <img v-if="t.logoUrl" :src="t.logoUrl" alt="" width="24" height="24" />
                  {{ t.name }}
                </h2>
                <p v-if="t.description" class="card-text text-muted small">{{ t.description }}</p>
                <div class="mt-auto d-flex flex-wrap gap-1 pt-2">
                  <span v-for="m in t.authMethods" :key="m.id" class="badge bg-primary">
                    {{ m.label ?? m.kind }}
                  </span>
                </div>
              </div>
              <div class="card-footer bg-transparent border-top-0 pt-0 pb-3">
                <button class="bb-btn-secondary-cta btn-sm w-100" @click.stop="pick(t)">
                  Select
                </button>
              </div>
            </div>
          </template>

          <template #empty-state>
            <div class="text-center py-5">
              <i class="bi bi-collection display-1 text-muted"></i>
              <h2 class="mt-3 text-muted">No templates available</h2>
              <p class="text-muted">No published connection templates were found.</p>
            </div>
          </template>
        </DataView>
      </PanelCard>
    </PanelGrid>
  </main>
</template>
